import { useCallback, useMemo } from 'react';
import {
  isModuleEnabledIn,
  isPageRowEnabledIn,
  useNavConfig,
  type NavConfigState,
} from '../../../api/Navigation/moduleConfig';
import { navigationModules } from '../../../components/layout/navigationModules';

/**
 * Which product modules this company actually has.
 *
 * The same question `useFilteredNavigation` asks before drawing the sidebar, answered from the same
 * rows: both read the navigation config store (CO-851). A module the Operator has never configured
 * counts as enabled, so only an explicit `isEnabled: false` row hides a real module and a company
 * with no config behaves exactly as it does today. A DEMO module is the exception the store makes
 * everywhere: it counts as off until a modules read has succeeded and returned rows.
 *
 * CO-853 adds the second half of the sidebar's rule: a module whose every page is closed is not a
 * module the company has, even with no OFF row on the module itself. `projects` is the case that
 * showed it: no module row, both of its pages demo surfaces, so the sidebar dropped the module while
 * the dashboard still fetched and drew its KPI group. See `modulesWithNoOpenPage`.
 *
 * This used to issue its own `navigation/modules` request with deps `[isLoggedIn]` and no
 * `ModuleConfigUpdated` subscription, so a failed read stayed "everything enabled" until a reload
 * and an Operator's change never reached an open dashboard. Reading the store fixes both and costs
 * no request.
 *
 * Kept as its own small hook rather than a `useFilteredNavigation()` call because the dashboard
 * needs the module set and nothing else. In particular it stays ROLE-BLIND: the answer is about the
 * company, so it must not be derived from the role-filtered sidebar list (a role that sees the
 * dashboard but not a module's pages would otherwise lose that module's numbers).
 */
export type UseEnabledModulesResult = {
  /** False until the company's config has been fetched (or the fetch has failed). */
  resolved: boolean;
  isModuleEnabled: (moduleId: string) => boolean;
};

type NavRows = Pick<NavConfigState, 'modules' | 'modulesOk' | 'pages' | 'pagesOk'>;

/**
 * Ids of the registry modules that have pages, none of which is open for this company: every page
 * carries an OFF row, is a demo surface the company was not granted (the server sends those as OFF
 * rows, and the store keeps them closed while a read is missing), or sits in a sub-module that is
 * itself off. Built from the registry, not from a list of demo keys, so a module added later needs
 * nothing here.
 *
 * `previewOnly` pages do not count either way: they are UI-only previews behind a separate runtime
 * flag, never a sign that the company has the module.
 *
 * A module id that is not in the registry is never returned, so `isModuleEnabled` falls back to the
 * module row alone for it.
 */
function modulesWithNoOpenPage(rows: NavRows): string[] {
  const closed: string[] = [];
  for (const module of navigationModules) {
    let counted = 0;
    let open = 0;
    for (const page of module.pages) {
      if (page.previewOnly) continue;
      counted += 1;
      if (isPageRowEnabledIn(rows, page.path)) open += 1;
    }
    for (const sub of module.subModules ?? []) {
      const subOn = isModuleEnabledIn(rows, sub.id);
      for (const page of sub.pages) {
        if (page.previewOnly) continue;
        counted += 1;
        if (subOn && isPageRowEnabledIn(rows, page.path)) open += 1;
      }
    }
    if (counted > 0 && open === 0) closed.push(module.id);
  }
  return closed;
}

const KEY_SEPARATOR = '|';

export function useEnabledModules(): UseEnabledModulesResult {
  const navConfig = useNavConfig();
  const { modules, modulesOk, pages, pagesOk } = navConfig.state;

  // A STRING on purpose. `useDashboardData` lists `isModuleEnabled` in the deps of the effect that
  // fetches the whole dashboard, so the function must not change identity when page rows changed
  // without changing the answer (an Operator switching one page of a module that keeps others, or a
  // background refresh). The list is recomputed on every page-row change, but the string it joins to
  // is compared by value, so the callback below only changes when a module opened or closed.
  const closedModulesKey = useMemo(
    () => modulesWithNoOpenPage({ modules, modulesOk, pages, pagesOk }).join(KEY_SEPARATOR),
    [modules, modulesOk, pages, pagesOk],
  );

  const isModuleEnabled = useCallback(
    (moduleId: string) => {
      if (!isModuleEnabledIn({ modules, modulesOk }, moduleId)) return false;
      return closedModulesKey === '' || !closedModulesKey.split(KEY_SEPARATOR).includes(moduleId);
    },
    [modules, modulesOk, closedModulesKey],
  );

  return { resolved: navConfig.settled, isModuleEnabled };
}

export default useEnabledModules;
